import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../services/api';
import { 
  ArrowTrendingUpIcon,
  UserGroupIcon,
  TrophyIcon,
  BoltIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config/platformConfig';

interface AnalyticsData {
  metrics: {
    totalLeads: number;
    newLeads: number;
    qualifiedLeads: number;
    enrolled: number;
    lost: number;
    conversionRate: number;
    avgLeadScore: number;
  };
  stageDistribution: Record<string, number>;
  sourcePerformance: Record<string, { total: number; enrolled: number }>;
}

interface AIAnalyticsDashboardProps {
  branchId: string;
}

const AIAnalyticsDashboard: React.FC<AIAnalyticsDashboardProps> = ({ branchId }) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [branchId, dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/analytics/branches/${branchId}/dashboard?dateRange=${dateRange}`,
        {
          headers: {
            'Authorization': `Bearer ${await getAuthToken()}`
          }
        }
      );
      const result = await response.json();
      setAnalytics(result.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center text-gray-500">No data available</div>;
  }

  const { metrics, stageDistribution, sourcePerformance } = analytics;

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CpuChipIcon className="w-7 h-7 text-purple-600" />
          AI-Powered Analytics
        </h2>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Leads"
          value={metrics.totalLeads}
          icon={<UserGroupIcon className="w-6 h-6" />}
          color="blue"
        />
        <MetricCard
          title="Conversion Rate"
          value={`${metrics.conversionRate}%`}
          icon={<ArrowTrendingUpIcon className="w-6 h-6" />}
          color="green"
          trend={metrics.conversionRate > 15 ? 'up' : 'down'}
        />
        <MetricCard
          title="Enrolled"
          value={metrics.enrolled}
          icon={<TrophyIcon className="w-6 h-6" />}
          color="purple"
        />
        <MetricCard
          title="Avg Lead Score"
          value={Math.round(metrics.avgLeadScore)}
          icon={<BoltIcon className="w-6 h-6" />}
          color="yellow"
        />
      </div>

      {/* Stage Distribution */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Pipeline</h3>
        <div className="space-y-3">
          {Object.entries(stageDistribution).map(([stage, count]) => (
            <div key={stage} className="flex items-center gap-3">
              <div className="w-32 text-sm font-medium text-gray-700 capitalize">
                {stage.replace('_', ' ')}
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                <div
                  className="bg-blue-600 h-6 rounded-full flex items-center justify-end px-2"
                  style={{ width: `${(count / metrics.totalLeads) * 100}%` }}
                >
                  <span className="text-xs font-semibold text-white">{count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Source Performance */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Source Performance</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Source</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Leads</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Enrolled</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sourcePerformance).map(([source, data]) => {
                const conversionRate = data.total > 0 
                  ? ((data.enrolled / data.total) * 100).toFixed(1)
                  : '0.0';
                return (
                  <tr key={source} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-sm text-gray-900 capitalize">
                      {source.replace('_', ' ')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">{data.total}</td>
                    <td className="py-3 px-4 text-sm text-gray-900 text-right">{data.enrolled}</td>
                    <td className="py-3 px-4 text-sm font-semibold text-right">
                      <span className={`${parseFloat(conversionRate) > 15 ? 'text-green-600' : 'text-gray-600'}`}>
                        {conversionRate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'yellow';
  trend?: 'up' | 'down';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color, trend }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        {trend && (
          <div className={`flex items-center gap-1 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />}
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

export default AIAnalyticsDashboard;
